import axios from 'axios';
import { DashboardStats, GameManagement, AdminApiResponse } from '../types';
import { API_CONFIG } from '../config/apiConfig';

const adminApi = axios.create({
  baseURL: API_CONFIG.adminBaseURL,
  timeout: API_CONFIG.authTimeout,
});

// Request interceptor for adding auth headers
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAccessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors and token refresh
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't redirect if already on login page or if it's a health check
      const currentPath = window.location.pathname;
      const isHealthCheck = originalRequest.url?.includes('/health');
      
      if (currentPath === '/login' || isHealthCheck) {
        return Promise.reject(error);
      }

      // Clear tokens and redirect to login
      localStorage.removeItem('adminAccessToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      console.error('Access forbidden - insufficient permissions');
      return Promise.reject(new Error('You do not have permission to perform this action'));
    }

    console.error('Admin API Error:', error);
    return Promise.reject(error);
  }
);

// API Methods
export const dashboardApi = {
  getStats: (): Promise<AdminApiResponse<DashboardStats>> => 
    adminApi.get('/dashboard/stats').then(res => res.data),
};

export const usersApi = {
  getAllUsers: (page = 1, limit = 10, search?: string, filter?: any, createdAtFrom?: string, createdAtTo?: string): Promise<any> => {
    let url = `/users?page=${page}&limit=${limit}`;
    if (search) url += `&search=${search}`;
    if (filter) url += `&filter=${JSON.stringify(filter)}`;
    if (createdAtFrom) url += `&createdAtFrom=${createdAtFrom}`;
    if (createdAtTo) url += `&createdAtTo=${createdAtTo}`;
    return adminApi.get(url).then(res => res.data);
  },
  
  getUserById: (id: string): Promise<any> =>
    adminApi.get(`/users/${id}`).then(res => res.data),
  
  createUser: (data: any): Promise<any> =>
    adminApi.post('/users', data).then(res => res.data),
  
  updateUser: (id: string, data: any): Promise<any> =>
    adminApi.put(`/users/${id}`, data).then(res => res.data),
  
  deleteUser: (id: string): Promise<any> =>
    adminApi.delete(`/users/${id}`).then(res => res.data),

  getUserStatistics: (): Promise<any> =>
    adminApi.get('/users/statistics').then(res => res.data),

  updateUserStatus: (id: string, isActive: boolean): Promise<any> =>
    adminApi.put(`/users/${id}/status`, { isActive }).then(res => res.data),

  bulkUpdateUsers: (userIds: string[], field: string, value: any): Promise<any> =>
    adminApi.put('/users/bulk', { userIds, field, value }).then(res => res.data),

  exportUsers: (userIds: string[], format: string): Promise<any> =>
    adminApi.post('/users/export', { userIds, format }).then(res => res.data),

  updateUserEnergy: (id: string, energy: number): Promise<any> =>
    adminApi.put(`/users/${id}/energy`, { energy }).then(res => res.data),
};

export const gamesApi = {
  getAllGames: (page = 1, pageSize = 10): Promise<AdminApiResponse<GameManagement[]>> =>
    adminApi.get(`/games?page=${page}&pageSize=${pageSize}`).then(res => res.data),
  
  getGameById: (id: string): Promise<AdminApiResponse<GameManagement>> =>
    adminApi.get(`/games/${id}`).then(res => res.data),
  
  createGame: (data: Partial<GameManagement>): Promise<AdminApiResponse<GameManagement>> =>
    adminApi.post('/games', data).then(res => res.data),
  
  updateGame: (id: string, data: Partial<GameManagement>): Promise<AdminApiResponse<GameManagement>> =>
    adminApi.put(`/games/${id}`, data).then(res => res.data),
  
  deleteGame: (id: string): Promise<AdminApiResponse<void>> =>
    adminApi.delete(`/games/${id}`).then(res => res.data),
};

export const authApi = {
  login: (email: string, password: string): Promise<any> =>
    adminApi.post('/auth/login', { email, password }).then(res => res.data),
  
  logout: (): Promise<AdminApiResponse<void>> =>
    adminApi.post('/auth/logout').then(res => res.data),
};

export default adminApi;
